import { Dialog, DialogActions, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Close } from '@styled-icons/evaicons-solid/Close';
import useUploadState from 'FeatureModules/Document/hooks/useUploadState';
import ThemeButton from 'DesignSystem/Buttons/ThemeButton/ThemeButton';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { IDefaultDialogProps } from 'types/dialog.types';
import { IBaseDocument } from 'FeatureModules/Document/types/document.types';
import DocumentUpload from '../DocumentUpload';
import useUploadDialogActions from 'FeatureModules/Document/hooks/useUploadDialogActions';
import { UploadProvider } from 'FeatureModules/Document/Contexts/UploadContext';

interface IUploadDocumentDialogProps extends IDefaultDialogProps {
  /** Pre selects the site id and disables the site selection */
  defaultSiteId?: string;
  /** Pre selects the owned by id and disables the owned by selection */
  defaultOwnedById?: string;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  margin: theme.spacing(4),

  '& .MuiDialog-paperWidthXl': {
    maxHeight: '90vh',
    minWidth: 700,

    [theme.breakpoints.up('lg')]: {
      minWidth: '60%',
    },
  },

  '& .rdrCalendarWrapper': {
    position: 'absolute',
  },
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

const UploadContainer = styled('div')(({ theme }) => ({
  padding: theme.spacing(2.5, 4, 0),
  overflow: 'auto',
}));

const CloseButton = styled(IconButton)(() => ({
  position: 'absolute',
  top: 0,
  right: 0,
  zIndex: 1,
}));

const UploadDocumentDialog: FC<IUploadDocumentDialogProps> = ({
  open,
  defaultSiteId,
  defaultOwnedById,
  onClose,
}) => {
  const [selectedForDeletion, setSelectedForDeletion] = useState<string[]>([]);
  const { cleanFiles, handleCleanCreatedRecords } = useUploadState();
  const [documents, setDocuments] = useState<IBaseDocument[]>([]);
  const { handleSaveDocuments, handleCancelEditing } = useUploadDialogActions();

  useEffect(() => () => cleanFiles(), []); // eslint-disable-line

  // Make sure that all documents have the required fields before saving
  const disableSaveButton = useMemo(
    () => !documents.every(document => document.category),
    [documents]
  );

  const resetState = () => {
    setSelectedForDeletion([]);
    setDocuments([]);
    handleCleanCreatedRecords();
    onClose?.();
    cleanFiles();
  };

  const handleDeleteDocument = (id: string) => {
    setSelectedForDeletion([...selectedForDeletion, id]);
  };

  const cancelEditing = () => {
    handleCancelEditing({
      documents,
      deletedDocuments: selectedForDeletion,
    });
    resetState();
  };

  const onSubmit = () => {
    handleSaveDocuments({
      documents,
      deletedDocuments: selectedForDeletion,
    });
    resetState();
  };

  return (
    <UploadProvider>
      <StyledDialog open={!!open} onClose={cancelEditing} fullWidth maxWidth="xl">
        <CloseButton onClick={cancelEditing} data-testid="close-dialog-button">
          <Close size={23} />
        </CloseButton>
        <UploadContainer>
          <DocumentUpload
            deletedDocuments={selectedForDeletion}
            handleDelete={handleDeleteDocument}
            defaultSiteId={defaultSiteId}
            defaultOwnedById={defaultOwnedById}
            title="Add new documents"
            handleDocumentsChange={setDocuments}
            documents={documents}
          />
        </UploadContainer>
        <StyledDialogActions>
          <ThemeButton color="WHITE" onClick={cancelEditing} size="large">
            Cancel
          </ThemeButton>

          <Tooltip title="Make sure that all required fields are set">
            <div>
              <ThemeButton
                color="PRIMARY"
                data-testid="save-documents-btn"
                onClick={onSubmit}
                disabled={disableSaveButton}
                size="large"
              >
                Save
              </ThemeButton>
            </div>
          </Tooltip>
        </StyledDialogActions>
      </StyledDialog>
    </UploadProvider>
  );
};

export default UploadDocumentDialog;
